import { Box, withStyles } from '@material-ui/core';
import { SButton, SETVIImage, SText } from '@setvi/shared/components';
import ArrowBackIcon from '@material-ui/icons/ArrowBack';
import RefreshIcon from '@material-ui/icons/Refresh';
import { iOSMessages, useIOSCommunication } from '@setvi/shared/hooks';
import { ROUTES } from 'enumsV2';
import { useLocation, useRouteError } from 'react-router-dom';
import { useEffect, useMemo } from 'react';

const CHUNK_RELOAD_KEY = 'chunk_error_reload';
const MAX_RELOAD_ATTEMPTS = 1;

interface ErrorPageProps {
  src: string;
  title: string;
  description: string;
}

const isChunkLoadError = (error: unknown): boolean =>
  error instanceof Error &&
  (error.name === 'ChunkLoadError' ||
    error.message.includes('Loading chunk') ||
    error.message.includes('Loading CSS chunk'));

const Container = withStyles({
  root: {
    height: '100%',
    width: '100%',
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    gap: 30,
    '& button': {
      width: 175,
      height: 50
    }
  }
})(Box);

const TextWrapper = withStyles({
  root: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    gap: 10,
    width: '70%',
    textAlign: 'center'
  }
})(Box);

export const ErrorPage = ({ src, title, description }: ErrorPageProps) => {
  const location = useLocation();
  const routeError = useRouteError();
  const { sendMessageToIOS } = useIOSCommunication();

  const queryParams = new URLSearchParams(location.search);
  const ipadQueryParam = queryParams.get('isIpad');

  const isIpad = useMemo(() => ipadQueryParam === 'true', [ipadQueryParam]);
  const isChunkError = useMemo(
    () => isChunkLoadError(routeError),
    [routeError]
  );

  const handleReload = () => {
    sessionStorage.removeItem(CHUNK_RELOAD_KEY);
    window.location.reload();
  };

  const handleGoHome = () => {
    if (!isIpad) {
      window.location.href = ROUTES.HOME;
      return;
    }

    sendMessageToIOS(iOSMessages.Close);
    window?.location?.reload();
  };

  useEffect(() => {
    if (!isChunkError) return;

    const reloadCount = Number(sessionStorage.getItem(CHUNK_RELOAD_KEY) || 0);

    if (reloadCount < MAX_RELOAD_ATTEMPTS) {
      sessionStorage.setItem(CHUNK_RELOAD_KEY, String(reloadCount + 1));
      window.location.reload();
    }
  }, [isChunkError]);

  if (isChunkError) {
    return (
      <Container>
        <SETVIImage src="/images/error-page.svg" alt="Update available" />
        <TextWrapper>
          <SText noLineClamp weight="bold" size="3xl">
            New Update Available
          </SText>
          <SText noLineClamp fontColor="#777E90">
            A new version of the application is available. Please reload the
            page to get the latest updates.
          </SText>
        </TextWrapper>
        <SButton startIcon={<RefreshIcon />} onClick={handleReload}>
          Reload Page
        </SButton>
      </Container>
    );
  }

  return (
    <Container>
      <SETVIImage src={src} alt="Error" />
      <TextWrapper>
        <SText noLineClamp weight="bold" size="3xl">
          {title}
        </SText>
        <SText noLineClamp fontColor="#777E90">
          {description}
        </SText>
      </TextWrapper>
      <SButton startIcon={<ArrowBackIcon />} onClick={handleGoHome}>
        {isIpad ? 'Refresh' : 'Go to homepage'}
      </SButton>
    </Container>
  );
};
